import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, ChevronRight, LayoutList, Layers, Plus, Search, Trash2, Upload, Users, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { createInscricaoInput } from '@portal-leo/shared';
import type { z } from 'zod';
import { ImportInscricoesModal } from './ImportInscricoesModal';

type CreateInscricaoInput = z.infer<typeof createInscricaoInput>;

interface TabInscricoesProps {
  eventoId: string;
  valorInscricao: string | null;
  limiteParticipantes: number | null;
}

export function TabInscricoes({ eventoId, valorInscricao, limiteParticipantes }: TabInscricoesProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('list');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [filtroClube, setFiltroClube] = useState('');
  const [filtroLote, setFiltroLote] = useState('');
  const [comRestricao, setComRestricao] = useState(false);
  const utils = trpc.useUtils();

  const isGrouped = viewMode === 'grouped';

  const { data, isLoading } = trpc.eventos.listInscricoes.useQuery({
    id: eventoId,
    page: isGrouped ? 1 : page,
    limit: isGrouped ? 500 : 20,
    search: search || undefined,
    clube: filtroClube || undefined,
    lote: filtroLote || undefined,
    comRestricaoAlimentar: comRestricao || undefined,
  }, { placeholderData: (prev) => prev });
  const { data: filtros } = trpc.eventos.getInscricoesFiltros.useQuery({ id: eventoId });
  const { data: financeiro } = trpc.eventos.getFinanceiro.useQuery({ id: eventoId });
  const inscricoes = data?.items;

  const resetPage = () => setPage(1);
  const filtrosAtivos = search || filtroClube || filtroLote || comRestricao;

  type Inscricao = NonNullable<typeof inscricoes>[number];

  const grupos = (() => {
    if (!isGrouped || !inscricoes) return [] as Array<{ clube: string; items: Inscricao[]; valorTotal: number; pagos: number }>;
    const map = new Map<string, { clube: string; items: Inscricao[]; valorTotal: number; pagos: number }>();
    for (const i of inscricoes) {
      const key = i.clube || '(Sem clube)';
      const g = map.get(key) ?? { clube: key, items: [], valorTotal: 0, pagos: 0 };
      g.items.push(i);
      g.valorTotal += Number(i.valor || 0);
      if (i.statusPagamento === 'PAGO') g.pagos += 1;
      map.set(key, g);
    }
    return Array.from(map.values()).sort((a, b) => a.clube.localeCompare(b.clube, 'pt-BR'));
  })();

  const toggleGroup = (clube: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(clube)) next.delete(clube);
      else next.add(clube);
      return next;
    });
  };

  const invalidateAll = () => {
    utils.eventos.listInscricoes.invalidate();
    utils.eventos.getById.invalidate({ id: eventoId });
    utils.eventos.getFinanceiro.invalidate({ id: eventoId });
    utils.livroCaixa.listLancamentos.invalidate({ eventoId });
    utils.livroCaixa.getResumoFinanceiro.invalidate({ eventoId });
    utils.livroCaixa.getBalancete.invalidate({ eventoId });
    utils.livroCaixa.getSaldosDiarios.invalidate({ eventoId });
    utils.contas.list.invalidate();
  };

  const addMutation = trpc.eventos.addInscricao.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseModal();
    },
  });

  const updateMutation = trpc.eventos.updateInscricao.useMutation({
    onSuccess: () => {
      invalidateAll();
    },
  });

  const removeMutation = trpc.eventos.removeInscricao.useMutation({
    onSuccess: () => {
      invalidateAll();
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateInscricaoInput>({
    resolver: zodResolver(createInscricaoInput),
    defaultValues: {
      eventoId,
      statusPagamento: 'PENDENTE',
    },
  });

  const statusPagamento = watch('statusPagamento');

  const onSubmit = (data: CreateInscricaoInput) => {
    addMutation.mutate(data);
  };

  const handleOpenModal = () => {
    reset({
      eventoId,
      nome: '',
      clube: '',
      restricaoAlimentar: '',
      lote: '',
      valor: '',
      statusPagamento: 'PENDENTE',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
  };

  const handleRemove = (id: string) => {
    if (window.confirm('Tem certeza que deseja remover esta inscrição?')) {
      removeMutation.mutate({ id });
    }
  };

  const handleTogglePagamento = (inscricao: any) => {
    const newStatus = inscricao.statusPagamento === 'PAGO' ? 'PENDENTE' : 'PAGO';
    updateMutation.mutate({
      id: inscricao.id,
      statusPagamento: newStatus,
    });
  };

  const formatCurrency = (value: string | number | null) => {
    if (!value) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      PENDENTE: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      PAGO: { label: 'Pago', className: 'bg-green-100 text-green-800' },
      CANCELADO: { label: 'Cancelado', className: 'bg-red-100 text-red-800' },
    };
    const info = statusMap[status] ?? statusMap.PENDENTE!;
    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${info.className}`}>
        {info.label}
      </span>
    );
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const totalInscritos = data?.meta.total ?? 0;
  const inscritosPagos = financeiro?.inscricoesPagas ?? 0;
  const vagasDisponiveis = limiteParticipantes ? limiteParticipantes - totalInscritos : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-wrap gap-y-2 md:gap-6">
          <div>
            <p className="text-sm text-gray-500">Total de inscritos</p>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              <span className="text-lg font-bold">
                {totalInscritos}
                {limiteParticipantes && ` / ${limiteParticipantes}`}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500">Pagamentos confirmados</p>
            <p className="text-lg font-bold text-green-600">{inscritosPagos}</p>
          </div>
          {valorInscricao && (
            <div>
              <p className="text-sm text-gray-500">Valor inscrição</p>
              <p className="text-lg font-bold">{formatCurrency(valorInscricao)}</p>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsImportOpen(true)}
            disabled={!canEdit || (vagasDisponiveis !== null && vagasDisponiveis <= 0)}
          >
            <Upload className="mr-2 h-4 w-4" />
            Importar Excel
          </Button>
          <Button
            onClick={handleOpenModal}
            disabled={!canEdit || (vagasDisponiveis !== null && vagasDisponiveis <= 0)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova Inscrição
          </Button>
        </div>
      </div>

      {vagasDisponiveis !== null && vagasDisponiveis <= 5 && vagasDisponiveis > 0 && (
        <div className="rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800">
          Restam apenas {vagasDisponiveis} vaga{vagasDisponiveis > 1 ? 's' : ''}!
        </div>
      )}

      {vagasDisponiveis !== null && vagasDisponiveis <= 0 && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
          Evento lotado! Não é possível adicionar novas inscrições.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <select
          value={filtroClube}
          onChange={(e) => { setFiltroClube(e.target.value); resetPage(); }}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Todos os clubes</option>
          {filtros?.clubes.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={filtroLote}
          onChange={(e) => { setFiltroLote(e.target.value); resetPage(); }}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Todos os lotes</option>
          {filtros?.lotes.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm">
          <input
            type="checkbox"
            checked={comRestricao}
            onChange={(e) => { setComRestricao(e.target.checked); resetPage(); }}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Com restrição
        </label>
        {filtrosAtivos && (
          <button
            onClick={() => { setSearch(''); setFiltroClube(''); setFiltroLote(''); setComRestricao(false); resetPage(); }}
            className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
            Limpar
          </button>
        )}
        <div className="ml-auto inline-flex overflow-hidden rounded-lg border border-gray-200 bg-white text-sm">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1 px-3 py-1.5 ${viewMode === 'list' ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'}`}
            aria-pressed={viewMode === 'list'}
          >
            <LayoutList className="h-4 w-4" />
            Lista
          </button>
          <button
            onClick={() => setViewMode('grouped')}
            className={`flex items-center gap-1 border-l border-gray-200 px-3 py-1.5 ${viewMode === 'grouped' ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'}`}
            aria-pressed={viewMode === 'grouped'}
          >
            <Layers className="h-4 w-4" />
            Agrupada por clube
          </button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {!inscricoes || inscricoes.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>Nenhuma inscrição cadastrada</p>
            </div>
          ) : isGrouped ? (
            <div className="divide-y divide-gray-100">
              {grupos.map((g) => {
                const expanded = expandedGroups.has(g.clube);
                return (
                  <div key={g.clube}>
                    <button
                      onClick={() => toggleGroup(g.clube)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        {expanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                        <span className="font-medium text-gray-900">{g.clube}</span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          {g.items.length}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-700">{g.pagos} pago{g.pagos === 1 ? '' : 's'}</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(g.valorTotal)}</span>
                      </div>
                    </button>
                    {expanded && (
                      <div className="bg-gray-50 px-2 pb-2">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              <TableHead>Lote</TableHead>
                              <TableHead>Restrição</TableHead>
                              <TableHead className="text-right">Valor</TableHead>
                              <TableHead>Pagamento</TableHead>
                              <TableHead className="text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {g.items.map((inscricao) => (
                              <TableRow key={inscricao.id}>
                                <TableCell className="font-medium">{inscricao.nome}</TableCell>
                                <TableCell>{inscricao.lote || '-'}</TableCell>
                                <TableCell>{inscricao.restricaoAlimentar || '-'}</TableCell>
                                <TableCell className="text-right">{formatCurrency(inscricao.valor)}</TableCell>
                                <TableCell>
                                  <button
                                    onClick={() => handleTogglePagamento(inscricao)}
                                    disabled={!canEdit || updateMutation.isPending}
                                    className={`flex items-center gap-2 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  >
                                    {getStatusBadge(inscricao.statusPagamento)}
                                  </button>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemove(inscricao.id)}
                                    disabled={!canEdit || removeMutation.isPending}
                                    className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                                  >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Clube</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead>Restrição</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inscricoes.map((inscricao) => (
                  <TableRow key={inscricao.id}>
                    <TableCell className="font-medium">{inscricao.nome}</TableCell>
                    <TableCell>{inscricao.clube || '-'}</TableCell>
                    <TableCell>{inscricao.lote || '-'}</TableCell>
                    <TableCell>{inscricao.restricaoAlimentar || '-'}</TableCell>
                    <TableCell className="text-right">{formatCurrency(inscricao.valor)}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleTogglePagamento(inscricao)}
                        disabled={!canEdit || updateMutation.isPending}
                        className={`flex items-center gap-2 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {getStatusBadge(inscricao.statusPagamento)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(inscricao.id)}
                        disabled={!canEdit || removeMutation.isPending}
                        className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {data && !isGrouped && (
            <div className="px-4 pb-2">
              <Pagination meta={data.meta} onPageChange={setPage} itemLabel="inscrições" />
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title="Nova Inscrição">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register('eventoId')} />

          <Input label="Nome *" {...register('nome')} error={errors.nome?.message} />

          <Input label="Clube" {...register('clube')} error={errors.clube?.message} />

          <div className="grid grid-cols-2 gap-4">
            <Input label="Lote" {...register('lote')} error={errors.lote?.message} />
            <Input label="Valor (R$)" type="number" step="0.01" {...register('valor')} error={errors.valor?.message} />
          </div>

          <Input label="Restrição alimentar" {...register('restricaoAlimentar')} error={errors.restricaoAlimentar?.message} />

          {valorInscricao && (
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
              <input
                type="checkbox"
                id="pagamento"
                {...register('statusPagamento', {
                  setValueAs: (v) => (v ? 'PAGO' : 'PENDENTE'),
                })}
                checked={statusPagamento === 'PAGO'}
                onChange={(e) => {
                  const value = e.target.checked ? 'PAGO' : 'PENDENTE';
                  reset({ ...watch(), statusPagamento: value });
                }}
                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="pagamento" className="flex-1 cursor-pointer">
                <p className="font-medium text-gray-900">Pagamento realizado</p>
                <p className="text-sm text-gray-500">Valor: {formatCurrency(valorInscricao)}</p>
              </label>
            </div>
          )}

          {valorInscricao && statusPagamento === 'PAGO' && (
            <p className="text-sm text-gray-500">
              * Uma conta a receber será criada automaticamente no módulo Financeiro
            </p>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={addMutation.isPending}>
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>

      <ImportInscricoesModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        eventoId={eventoId}
        onCommitted={invalidateAll}
      />
    </div>
  );
}
